import { Box, Code, Heading, Link, SimpleGrid, Spinner, Text, VStack } from "@chakra-ui/react";
import { useMemo } from "react";
import { Link as RouterLink, useParams } from "react-router-dom";
import { useCxMappingQuery } from "../shared/use-cx-mapping";
import { EhrConfig, ehrIdFromMappingSource, EHR_LIST } from "./ehr-config";
import EhrSearch from "./EhrSearchPage";

export default function EhrPage() {
  const { cxId } = useParams<{ cxId: string }>();
  const cxMappingQuery = useCxMappingQuery(cxId);
  const mappings = cxMappingQuery.data;

  const ehrCards: EhrConfig[] = useMemo(() => {
    if (!mappings) return [];
    const seen = new Set<string>();
    for (const m of mappings) {
      const ehrId = ehrIdFromMappingSource(m.source);
      if (ehrId) seen.add(ehrId);
    }
    return EHR_LIST.filter(e => seen.has(e.id));
  }, [mappings]);

  if (!cxId) {
    return <EhrSearch />;
  }

  if (cxMappingQuery.isLoading) {
    return <Spinner />;
  }

  if (cxMappingQuery.isError) {
    return (
      <Text color="red.500" fontSize="sm">
        Could not load CX mappings. Try again or check the network.
      </Text>
    );
  }

  if (!mappings || ehrCards.length === 0) {
    return <Text color="gray.600">No EHR integrations found for this customer.</Text>;
  }

  return (
    <Box>
      <VStack p={10} align="start" spacing={6}>
        <Link as={RouterLink} to="/ehr" color="blue.500">
          Go to EHR Search
        </Link>
        <Box>
          <Heading size="lg" mb={2}>
            EHR Integrations
          </Heading>
          <Text color="gray.600" fontSize="sm">
            Customer: <Code>{cxId}</Code>
          </Text>
        </Box>
        <SimpleGrid columns={{ base: 1, sm: 2, md: 3 }} spacing={6} w="full" maxW="4xl">
          {ehrCards.map(ehr => {
            return (
              <Box
                as={RouterLink}
                to={`/ehr/${encodeURIComponent(cxId)}/${ehr.id}`}
                key={ehr.id}
                p={6}
                bg="white"
                borderRadius="lg"
                border="1px solid"
                borderColor="gray.200"
                display="block"
                transition="all 0.2s"
                _hover={{ shadow: "md", borderColor: "blue.300", textDecoration: "none" }}
                _focusVisible={{ boxShadow: "outline" }}
              >
                <Text fontWeight="bold" fontSize="lg" mb={1}>
                  {ehr.name}
                </Text>
                <Text color="gray.500" fontSize="sm">
                  {ehr.description}
                </Text>
              </Box>
            );
          })}
        </SimpleGrid>
      </VStack>
    </Box>
  );
}
